import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { requestAccountInfoAsyncAction, selectSelectedProvider } from 'rdx/account';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { GlobalAppState } from 'rdx/state';
import { setDeviceType } from 'rdx/theme/actions';
import { selectDeviceType } from 'rdx/theme/selectors';

import { requestTargetingParametersAsyncAction } from './actions';

const requestTargetingDeviceTypeDependencies = (
  action$: Observable<GlobalAppActions>,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf([requestAccountInfoAsyncAction.success, setDeviceType])),
    switchMap((action) => {
      const safeRefresh = isActionOf(setDeviceType)(action);
      return of(requestTargetingParametersAsyncAction.request({ safeRefresh }));
    }),
  );
};

const requestTargetingParametersEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestTargetingParametersAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const selectedProvider = selectSelectedProvider(state);
      const deviceType = selectDeviceType(state);

      if (!selectedProvider?.providerId) {
        return of(
          requestTargetingParametersAsyncAction.failure(new Error('Missing selected provider id')),
        );
      }

      return from(
        apiClient.deliveryProviderPortal.getTargetingValuesForProvider({
          provider_id: selectedProvider.providerId,
          city_id: selectedProvider.cityId,
          country_code: selectedProvider.countryCode,
          signup_source: selectedProvider.signup_source,
          provider_status: selectedProvider.lifecycle_status,
          device_type: deviceType,
        }),
      ).pipe(
        map((response) => requestTargetingParametersAsyncAction.success(response)),
        catchError((err) => of(requestTargetingParametersAsyncAction.failure(err))),
      );
    }),
  );
};

export const targetingParametersEpic = combineEpics(
  requestTargetingDeviceTypeDependencies,
  requestTargetingParametersEpic,
);
